import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import FormReservation from '../../components/FormReservation';
import Horaires from '../../components/Horaires';
import Map from '../../components/Map';
import { contentQueries, useGetContent } from '../../lib/react-query/ContentQueries';
import { meetingQueries, useGetMeetingById } from '../../lib/react-query/MeetingQueries';
import { productQueries, useGetAllProducts } from '../../lib/react-query/ProductQueries';
import { serviceQueries, useGetAllServices } from '../../lib/react-query/ServiceQueries';
import type { Product } from '../../types/product.types';

const searchParamsSchema = z.object({
  prestation: z.string().optional().catch(undefined),
  id: z.string().optional().catch(undefined),
  token: z.string().optional().catch(undefined),
  step: z.enum(['date', 'prestation']).optional().catch(undefined),
});

export const Route = createFileRoute('/rendez-vous/')({
  component: MeetingPage,
  validateSearch: searchParamsSchema,
  loader: async ({ context }) => {
    const { queryClient } = context;

    // Preload content, products, services, and meetings in parallel
    await Promise.all([
      queryClient.ensureQueryData(contentQueries.main()),
      queryClient.ensureQueryData(productQueries.all()),
      queryClient.ensureQueryData(serviceQueries.all()),
      queryClient.ensureQueryData(meetingQueries.fromToday()),
    ]);
  },
  head: () => ({
    meta: [
      {
        title: 'Rendez-vous | Azelysse Piercing & Tattoo',
      },
      {
        name: 'description',
        content:
          "Rendez-vous en ligne pour votre piercing ou tatouage chez Azelysse à Égly (91). Proche d'Arpajon, Brétigny-sur-Orge, Montlhéry. Choisissez votre prestation et réservez facilement.",
      },
      {
        property: 'og:title',
        content: 'Rendez-vous | Azelysse Piercing & Tattoo',
      },
      {
        property: 'og:description',
        content:
          "Rendez-vous en ligne pour votre piercing ou tatouage chez Azelysse à Égly (91). Proche d'Arpajon, Brétigny, Montlhéry. Réservez facilement.",
      },
      {
        property: 'og:url',
        content: 'https://azelysse.fr/rendez-vous',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://azelysse.fr/rendez-vous' }],
  }),
});

function MeetingPage() {
  const { prestation, id, token, step } = Route.useSearch();
  const navigate = useNavigate();
  const { data: content } = useGetContent();
  const { data: products = [] } = useGetAllProducts();
  const { data: services = [] } = useGetAllServices();
  const { data: meeting, isError, isFetching } = useGetMeetingById(id || '');

  // If an ID was provided but the meeting fetch failed, redirect to clean create mode
  useEffect(() => {
    if (id && isError && !isFetching) {
      navigate({ to: '/rendez-vous' });
    }
  }, [id, isError, isFetching, navigate]);

  if (!content) {
    return null;
  }

  return (
    <div className="mx-auto max-w-400 px-6 py-16">
      <div className="grid-cols-12 gap-8 xl:grid">
        <div className="xl:col-span-8">
          <h1 className="mb-8 text-2xl font-semibold">
            {meeting ? 'Modifier votre' : 'Prendre un'} rendez-vous
          </h1>
          <FormReservation
            selectedProduct={prestation as Product['slug']}
            content={content}
            products={products}
            services={services}
            meeting={meeting || undefined}
            token={token || meeting?.access_token}
            initialStep={step}
          />
        </div>
        <div className="hidden lg:col-span-3 lg:col-start-10 xl:block">
          <Map height="h-[300px]" />
          <div className="mt-8">
            <Horaires />
          </div>
        </div>
      </div>
    </div>
  );
}
